import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Sunrise, Plus } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useCurrency } from '@/contexts/CurrencyContext'
import apiClient, { ApiClientError } from '@/api/client'
import { toastHelpers } from '@/lib/toast-helpers'
import { NumericKeypad } from './NumericKeypad'
import { HelpPopover } from './HelpPopover'

interface OpenDayDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Set when the dialog was raised by a blocked sale rather than opened
   * deliberately (counter hit 409 `day_not_open`). Renders an explanation so
   * the till operator understands why checkout stopped — F-CTRL-N32 removed
   * auto-open, so the day's first sale waits on this dialog.
   */
  blockedReason?: string
  /**
   * Counter-tier session: confirm the tenant's standing float, don't choose a
   * number. No input, no keypad — one button.
   *
   * F-CTRL-N32. Expected cash at close is `opening_float + cash_sales`, so
   * understating the float is self-concealing theft: declare Rs 0 on a drawer
   * holding Rs 10,000 and exactly that much can walk with the day still
   * reconciling to the paisa. The backend enforces the same rule
   * (`opening_float_locked`) — this only removes the affordance.
   */
  confirmOnly?: boolean
}

export function OpenDayDialog({
  open,
  onOpenChange,
  blockedReason,
  confirmOnly = false,
}: OpenDayDialogProps) {
  const { formatCurrency, currencyCode } = useCurrency()
  const qc = useQueryClient()
  const [openingCash, setOpeningCash] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [showNotes, setShowNotes] = useState(false)

  // F-CTRL-N32: the tenant's standing float, pre-filled so the manager confirms
  // a familiar number instead of retyping it. This is the ONLY place a float is
  // suggested — a day the system auto-opens starts at Rs 0, because nobody has
  // counted that drawer.
  //
  // F-CTRL-N34: `staleTime: 0` + `refetchOnMount: 'always'`, deliberately. This
  // query used to cache for five minutes, which meant a float changed in
  // Settings was not re-read until the cache aged out — and on the desktop app
  // the `settings` prefix is persisted to IndexedDB for 24h (see
  // routes/__root.tsx), so the old number could survive app restarts for a day.
  // It fires once per dialog open and reads a single settings row; correctness
  // is worth far more than the round trip.
  const floatQuery = useQuery({
    queryKey: ['settings', 'default_opening_float'],
    queryFn: () => apiClient.getSetting('default_opening_float'),
    enabled: open,
    staleTime: 0,
    refetchOnMount: 'always',
  })
  const standingFloat = (() => {
    const raw = floatQuery.data?.data
    const n = typeof raw === 'number' ? raw : Number(raw)
    return Number.isFinite(n) && n >= 0 ? n : 0
  })()

  // Has the manager typed in the field? Gates the pre-fill below. Reset on open.
  const [floatEdited, setFloatEdited] = useState(false)

  useEffect(() => {
    if (open) {
      setOpeningCash('')
      setNotes('')
      setShowNotes(false)
      setFloatEdited(false)
    }
  }, [open])

  /**
   * Track the standing float until the manager takes over the field.
   *
   * F-CTRL-N34 — the bug this replaces. The old version latched: a
   * `prefillApplied` flag was set the first render `floatQuery.isSuccess` was
   * true, and every later value was then discarded. TanStack serves cached data
   * with status 'success' *immediately* while it revalidates in the background,
   * so with anything in the cache the flag was set on the STALE value and the
   * fresh one never landed. The first float change a tenant ever made worked
   * (cold cache → the query was pending → the real value arrived first); every
   * change after that silently pre-filled the old number, and the day was
   * opened with it.
   *
   * Gating on "did a human touch this field" instead of "have we run once"
   * keeps the original protection — a slow query must never overwrite what the
   * manager is typing — while letting a corrected value through.
   *
   * A standing float of 0 clears the field rather than pre-filling "0", which
   * also stops a stale non-zero value surviving a change to 0.
   */
  useEffect(() => {
    if (!open || floatEdited || !floatQuery.isSuccess) return
    setOpeningCash(standingFloat > 0 ? String(standingFloat) : '')
  }, [open, floatEdited, floatQuery.isSuccess, standingFloat])

  /**
   * Every user-driven change to the field goes through here — the text input,
   * the keypad, and the "Use this" shortcut. Marking the field as edited is
   * what stops a late-arriving float from overwriting a manager mid-count.
   */
  const editFloat = (next: string) => {
    setFloatEdited(true)
    setOpeningCash(next)
  }

  const openMut = useMutation({
    mutationFn: () =>
      apiClient.openBusinessDay({
        // Confirm-only sessions send the standing float verbatim, never the
        // (hidden) input — so the value posted always matches what the button
        // said, and always matches what the backend will accept.
        opening_cash: confirmOnly ? standingFloat : Number(openingCash) || 0,
        notes: confirmOnly ? undefined : notes.trim() || undefined,
      }),
    onSuccess: () => {
      toastHelpers.success('Day started')
      qc.invalidateQueries({ queryKey: ['day-ops'] })
      qc.invalidateQueries({ queryKey: ['currentDayStatus'] })
      onOpenChange(false)
    },
    onError: (err: Error) => {
      // Two tills can tap "Start the day" within seconds of each other at
      // opening — now more likely, since F-CTRL-N32 makes this the gate on the
      // first sale and counter staff can do it themselves. The loser of that
      // race gets 409 already_open, which is a success from their point of
      // view: the day they wanted now exists. Refresh and close rather than
      // showing them an error for something that worked.
      if (err instanceof ApiClientError && err.code === 'already_open') {
        toastHelpers.info('Day already started', 'Someone else just started today.')
        qc.invalidateQueries({ queryKey: ['day-ops'] })
        qc.invalidateQueries({ queryKey: ['currentDayStatus'] })
        onOpenChange(false)
        return
      }
      toastHelpers.apiError('Start the day', err)
    },
  })

  const parsedFloat = Number(openingCash)
  // Confirm-only renders no input, so text validity is meaningless — the gate
  // is simply "do we know the standing float yet". Without this branch a tenant
  // whose float is legitimately Rs 0 (banks its takings nightly) would leave
  // openingCash as '' and the Start button disabled forever.
  //
  // F-CTRL-N34: also wait out an in-flight revalidation. The counter tier posts
  // `standingFloat` verbatim, so tapping Start while a stale cached value is
  // still being refreshed would send the old number — and the backend, which
  // compares against the live setting, would reject it with
  // `opening_float_locked`. Better a button that is briefly disabled than a
  // permission error the cashier cannot act on. A failed background refresh
  // leaves the cached value in place with isFetching false, so an offline till
  // still degrades to "start with the last known float" rather than locking up.
  const isValid = confirmOnly
    ? floatQuery.isSuccess && !floatQuery.isFetching
    : openingCash.trim() !== '' && Number.isFinite(parsedFloat) && parsedFloat >= 0

  return (
    <Dialog open={open} onOpenChange={(o) => !openMut.isPending && onOpenChange(o)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Sunrise className="h-6 w-6 text-amber-500" aria-hidden="true" />
            Start the day
          </DialogTitle>
          <DialogDescription className="text-base">
            {confirmOnly
              ? "Check the drawer holds your usual float, then start the day. If it doesn't match, ask a manager to start it instead."
              : "How much cash is in the drawer right now? We'll use this to check the totals at the end of the day."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {blockedReason && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-950/40">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                Today hasn't been started yet
              </p>
              <p className="mt-1 text-sm leading-relaxed text-amber-800 dark:text-amber-200">
                {confirmOnly
                  ? "The sale can't go through until the day is started. Start it below, then take the order again — it's still in the cart."
                  : "The sale can't go through until the day is started. Count what's in the drawer now, enter it below, then take the order again — it's still in the cart."}
              </p>
            </div>
          )}

          {/* F-CTRL-N32: counter tier confirms the standing float, it does not
              choose one. No input, no keypad — understating the float is
              self-concealing theft (expected = float + sales). The backend
              rejects any other amount with `opening_float_locked`. */}
          {confirmOnly ? (
            <div className="space-y-3">
              <div className="rounded-lg border bg-muted/40 px-4 py-5 text-center">
                <p className="text-sm text-muted-foreground">Starting cash ({currencyCode})</p>
                <p className="mt-1 text-4xl font-bold tabular-nums">
                  {formatCurrency(standingFloat)}
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Your restaurant's usual float
                </p>
              </div>
              <p className="text-sm text-muted-foreground">
                Only a manager can start the day with a different amount.
              </p>
            </div>
          ) : (
            <>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="opening-cash" className="text-base">
                Starting cash ({currencyCode})
              </Label>
              <HelpPopover label="What is starting cash?">
                <span className="font-medium">Starting cash</span> is the money that was in the
                drawer before the first sale of the day.
              </HelpPopover>
            </div>
            <Input
              id="opening-cash"
              type="text"
              inputMode="decimal"
              autoFocus
              value={openingCash}
              onChange={(e) => editFloat(e.target.value.replace(/[^0-9.]/g, ''))}
              className="h-14 text-3xl font-semibold tabular-nums"
            />
            {/* F-CTRL-N32: this used to read "Yesterday ended with X — Use this",
                offering the previous day's counted cash as today's float. For any
                restaurant that banks its takings overnight that money is in the
                safe, not the till, and accepting it guaranteed a shortage of
                exactly X at close. We now suggest the tenant's own standing
                float, and say plainly that it still has to be verified. */}
            {standingFloat > 0 && (
              <div className="flex items-center justify-between gap-3 rounded-lg bg-muted/50 px-3 py-2">
                <span className="text-sm">
                  Your usual float is{' '}
                  <span className="font-semibold tabular-nums">
                    {formatCurrency(standingFloat)}
                  </span>
                  . Check the drawer actually holds it.
                </span>
                {openingCash !== String(standingFloat) && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => editFloat(String(standingFloat))}
                  >
                    Use this
                  </Button>
                )}
              </div>
            )}
            {!isValid && (
              <p className="text-sm text-muted-foreground">
                Type 0 if the drawer is empty right now.
              </p>
            )}
          </div>

          <NumericKeypad value={openingCash} onChange={editFloat} />

          {showNotes ? (
            <div className="space-y-2">
              <Label htmlFor="open-day-notes" className="text-base">
                Note for the owner (optional)
              </Label>
              <Textarea
                id="open-day-notes"
                rows={2}
                placeholder="Anything the owner should know — e.g. half-day shift, missing key…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowNotes(true)}
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <Plus className="h-3.5 w-3.5" /> Add a note (optional)
            </button>
          )}
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            size="lg"
            className="h-12 flex-1"
            onClick={() => onOpenChange(false)}
            disabled={openMut.isPending}
          >
            Cancel
          </Button>
          <Button
            size="lg"
            className="h-12 flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => openMut.mutate()}
            disabled={!isValid || openMut.isPending}
          >
            {openMut.isPending ? 'Saving…' : 'Start the day'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
